import React, { useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "kenosis_admin_token";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const formatError = (detail) => {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
};

const shell = "min-h-screen bg-[#0a0a0a] text-[#f4f1ed] font-sans antialiased";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [subs, setSubs] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/subscribers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setSubs(data);
      } catch (e) {
        if (e.response?.status === 401 || e.response?.status === 403) {
          localStorage.removeItem(TOKEN_KEY);
          if (!cancelled) { setToken(""); setError("Session expired. Please sign in again."); }
        } else if (!cancelled) {
          setError(formatError(e.response?.data?.detail) || e.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const onLogin = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch (e) {
      setError(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(""); setSubs(null); setEmail(""); setError("");
  };

  const downloadCsv = () => {
    if (!token) return;
    window.location.href = `${API}/admin/subscribers.csv?token=${encodeURIComponent(token)}`;
  };

  const filtered = subs
    ? subs.filter((s) => {
        if (!q.trim()) return true;
        const needle = q.toLowerCase();
        return [s.name, s.email, s.language, s.createdAt].some((v) => (v || "").toString().toLowerCase().includes(needle));
      })
    : [];

  return (
    <div className={shell} data-testid="admin-page">
      <Helmet>
        <title>Admin — Kenosis Method</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="border-b border-white/10 px-6 md:px-10 py-6 flex items-center justify-between">
        <div>
          <p className="text-[0.6rem] uppercase tracking-[0.35em] text-[#c6a87c]">Kenosis Method</p>
          <h1 className="mt-1 font-serif text-2xl text-[#f4f1ed]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Admin</h1>
        </div>
        {token && (
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="border border-white/20 px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-[#c6a87c] hover:text-[#c6a87c] transition"
          >
            Sign out
          </button>
        )}
      </header>

      <main className="px-6 md:px-10 py-10 md:py-14 max-w-[1400px] mx-auto">
        {!token ? (
          <div className="max-w-md">
            <h2 className="font-serif text-3xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Sign in</h2>
            <p className="text-sm text-white/60 mb-8">Administrator access only.</p>
            <form onSubmit={onLogin} data-testid="admin-login-form" className="space-y-6">
              <div className="flex flex-col">
                <label className="text-[0.65rem] uppercase tracking-[0.2em] text-[#c6a87c]/80">Email</label>
                <input
                  data-testid="admin-email"
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-3 border-b border-white/20 bg-transparent py-3 focus:border-[#c6a87c] focus:outline-none"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-[0.65rem] uppercase tracking-[0.2em] text-[#c6a87c]/80">Password</label>
                <input
                  data-testid="admin-password"
                  type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-3 border-b border-white/20 bg-transparent py-3 focus:border-[#c6a87c] focus:outline-none"
                />
              </div>
              {error && <p data-testid="admin-error" className="text-sm text-red-400">{error}</p>}
              <button
                data-testid="admin-submit"
                type="submit" disabled={busy}
                className="border border-[#c6a87c]/60 px-8 py-3 text-xs uppercase tracking-[0.2em] text-[#c6a87c] hover:bg-[#c6a87c] hover:text-black transition disabled:opacity-50"
              >
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </div>
        ) : (
          <>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
              <div>
                <p className="text-[0.6rem] uppercase tracking-[0.3em] text-[#c6a87c]">Journal sign-ups</p>
                <h2 className="mt-2 font-serif text-3xl md:text-4xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {subs === null ? "Loading…" : `${filtered.length} of ${subs.length}`}
                </h2>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  data-testid="admin-search"
                  value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, email, language…"
                  className="border border-white/15 bg-transparent px-4 py-2 text-sm placeholder:text-white/30 focus:border-[#c6a87c] focus:outline-none"
                />
                <button
                  data-testid="admin-export-csv"
                  onClick={downloadCsv}
                  className="border border-[#c6a87c]/60 px-6 py-2 text-xs uppercase tracking-[0.2em] text-[#c6a87c] hover:bg-[#c6a87c] hover:text-black transition"
                >
                  Export CSV
                </button>
              </div>
            </div>

            {error && <p data-testid="admin-error" className="mb-4 text-sm text-red-400">{error}</p>}

            <div className="border border-white/10">
              <table className="w-full text-sm" data-testid="admin-subscribers-table">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.02]">
                    <th className="text-left px-4 py-3 text-[0.65rem] uppercase tracking-[0.2em] text-[#c6a87c] w-1/4">Name</th>
                    <th className="text-left px-4 py-3 text-[0.65rem] uppercase tracking-[0.2em] text-[#c6a87c]">Email</th>
                    <th className="text-left px-4 py-3 text-[0.65rem] uppercase tracking-[0.2em] text-[#c6a87c] w-24">Lang</th>
                    <th className="text-left px-4 py-3 text-[0.65rem] uppercase tracking-[0.2em] text-[#c6a87c] w-56">Signed up</th>
                  </tr>
                </thead>
                <tbody>
                  {subs === null && (
                    <tr><td colSpan={4} className="px-4 py-10 text-center text-white/50">Loading…</td></tr>
                  )}
                  {subs !== null && filtered.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-10 text-center text-white/50">No sign-ups match.</td></tr>
                  )}
                  {filtered.map((s, i) => (
                    <tr key={s.id || i} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-row-${i}`}>
                      <td className="px-4 py-3 text-white/90">{s.name || "—"}</td>
                      <td className="px-4 py-3 text-white/90">{s.email}</td>
                      <td className="px-4 py-3 text-white/60 uppercase">{s.language || "—"}</td>
                      <td className="px-4 py-3 text-white/60">{formatDate(s.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
